import React, { useState } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import { useCorelConnection } from '../hooks/useCorelConnection';
import { ConnectionIndicator } from './ConnectionIndicator';
import { Button } from './ui';
import { LogOut } from 'lucide-react';
import { MessagesBadge } from './Messages/MessagesBadge';
import { MessagesModal } from './Messages/MessagesModal';
import { useMessages } from '../hooks/useMessages';
import { useMessageUrgency } from '../hooks/useMessageUrgency';

interface GlobalFooterProps {
  onLogout?: () => void;
  logoutLabel?: string;
  leftSlot?: React.ReactNode;
}

export function GlobalFooter({ onLogout, logoutLabel, leftSlot }: GlobalFooterProps) {
  const { userInfo, role } = usePermissions();
  const corelConnection = useCorelConnection();
  const { unreadCount, messages } = useMessages();
  const { sortByUrgency } = useMessageUrgency();
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  const hasUrgent = React.useMemo(() => {
    const sorted = sortByUrgency(messages);
    return sorted.some(m => (m.prioridade === 'urgente' || m.prioridade === 'alta') && !m.lida);
  }, [messages, sortByUrgency]);

  const getRoleColor = () => {
    switch (role) {
      case 'admin':
        return 'text-gold-400';
      case 'vendedor':
        return 'text-blue-400';
      case 'revenda':
        return 'text-purple-400';
      case 'cliente':
        return 'text-orange-400';
      case 'producao':
        return 'text-red-400';
      case 'clientes_plus':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const displayName = userInfo?.nome || 'Usuario';
  const roleLabel = role || '-';

  return (
    <>
      <footer className="relative px-3 py-2 border-t border-navy-700 bg-navy-900/95 backdrop-blur-sm min-h-[44px] flex items-center z-30">
        <div className="flex items-center justify-between gap-2 w-full">
          {leftSlot && (
            <div className="flex items-center flex-shrink-0">
              {leftSlot}
            </div>
          )}

          <div className="flex items-center gap-2 flex-1 min-w-0">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium text-gray-400 bg-navy-800/50 border border-navy-700/50 flex-shrink-0">
              {roleLabel}
            </span>

            <span className={`text-xs font-medium capitalize truncate ${getRoleColor()}`}>
              {displayName}
            </span>

            <ConnectionIndicator
              status={corelConnection.status}
              latencyMs={corelConnection.latencyMs}
              onClick={corelConnection.retry}
              size="sm"
            />
          </div>

          <div className="flex items-center gap-2 flex-shrink-0 relative z-40">
            <MessagesBadge
              unreadCount={unreadCount}
              onClick={() => setShowMessagesModal(true)}
              hasUrgent={hasUrgent}
            />

            {onLogout && (
              logoutLabel ? (
                <Button
                  variant="ghost"
                  onClick={onLogout}
                  icon={LogOut}
                  size="sm"
                  title={logoutLabel}
                  className="px-2.5 flex-shrink-0 whitespace-nowrap"
                >
                  {logoutLabel}
                </Button>
              ) : (
                <Button
                  variant="ghost"
                  onClick={onLogout}
                  icon={LogOut}
                  size="sm"
                  title="Sair"
                  className="p-1.5 flex-shrink-0"
                />
              )
            )}
          </div>
        </div>
      </footer>

      <MessagesModal
        isOpen={showMessagesModal}
        onClose={() => setShowMessagesModal(false)}
      />
    </>
  );
}
